import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setReportSSOPayrollList } from "../../slices/reportSlice";
import { generateRandomString } from "utils/helper";
import * as services from "../../services/reportApi";
import * as slices from "../../../employee/slices/employeeSlice";

function useSSOPaymentReport() {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [positionOptions, setPositionOptions] = useState([]);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [showReport, setShowReport] = useState(false);
    const [search, setSearch] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const reportSSOPayrollList = useSelector((state) => state.report.reportSSOPayroll || []);
    const branchOptions = useSelector((state) => state.employee.branchOptions);

    const showModal = () => {
        setIsModalOpen(true);
    };

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, positionOptions]);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, departmentListOption]);

    const useFetchOptions = (serviceFunction, companyId, groupType, dispatchFunction) => {
        const dispatch = useDispatch();
        useEffect(() => {
            const fetchData = async () => {
                dispatch(slices.setIsLoading(true));
                try {
                    const response = await serviceFunction({
                        requestId: generateRandomString(),
                        companyId,
                        groupType,
                    });
                    dispatch(dispatchFunction(response?.data));
                } catch (error) {
                    console.error('Failed to fetch data:', error);
                } finally {
                    dispatch(slices.setIsLoading(false));
                }
            };

            if (companyId) {
                fetchData();
            }
        }, [companyId, serviceFunction, groupType, dispatchFunction, dispatch]);
    };
    useFetchOptions(services.getBranch, currentCompany, "", slices.setBranchOptions);

    const modifiedOptions = (options) =>
        options?.map((opt) => ({
            ...opt,
            label: i18n.language === "en" ? (opt?.nameEn ? opt?.nameEn : opt.name) : opt.name,
            value: opt.id,
        }))?.sort((a, b) =>
            a.label.localeCompare(b.label, i18n.language === "en" ? "en" : "th", {
                sensitivity: "base",
            })
        );

    const modifiedBranchOptions = useMemo(() => modifiedOptions(branchOptions), [branchOptions, i18n.language]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
            // alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const getSSOPaymentTableReport = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getSSOPayrollReport({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    informationType: 1,
                    ...params.pagination,
                });

                dispatch(setReportSSOPayrollList(response?.data?.page?.content));

                setPagination({
                    ...pagination,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response?.data?.page?.totalElements,
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, pagination]
    );

    const exportSSOPayrollReport = useCallback(
        async (params = {}) => {
            console.log("params", search);
            try {
                dispatch(setIsLoading(true));
                services.ExportSSOPayrollReport({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy
                }).then(res => {
                    const url = window.URL.createObjectURL(res?.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', "social-security-payment-report.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, search]
    );

    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            setPagination({ ...pagination, ...modifiedParams?.pagination })
            getSSOPaymentTableReport({ ...modifiedParams, request: pagination.request, ...search });
        },
        [getSSOPaymentTableReport, pagination, search]
    );

    const handleSearch = (values) => {
        setShowReport(true)
        setSearch({})
        try {
            const { month, ...rest } = values;

            const searchValues = {
                ...rest,
                year: month.year(),
                month: month.month() + 1
            };

            setSearch(searchValues);

            getSSOPaymentTableReport({
                ...searchValues,
                ...pagination,
            });
        } catch (error) {
            console.log("error", error);
        }
    };

    useEffect(() => {
        if (currentCompany) {
            getPosition();
            getDepartment();
        }
    }, [currentCompany, getPosition, getDepartment])

    return {
        handleSearch,
        onChange,
        setIsModalOpen,
        showModal,
        isModalOpen,
        showReport,
        pagination,
        positionOptions: modifiedPositionOptions,
        departmentOptions: modifiedDepartmentOption,
        reportSSOPayrollList,
        exportSSOPayrollReport,
        branchOptions: modifiedBranchOptions,
    }
}
export default useSSOPaymentReport;